import { useCallback, useEffect, useRef, useState } from 'react';

/** Return from an async handler to keep isPending true after resolve (e.g. full-page redirect). */
export const KEEP_PENDING = Symbol('useAsyncAction.keepPending');

/**
 * Wraps an async function with pending state and duplicate-call protection.
 *
 * @template {(...args: unknown[]) => Promise<unknown>} T
 * @param {T | undefined | null} asyncFn
 * @returns {{ execute: (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>> | undefined>, isPending: boolean, error: unknown, reset: () => void }}
 */
export function useAsyncAction(asyncFn) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState(null);
  const isPendingRef = useRef(false);
  const abortRef = useRef(null);
  const asyncFnRef = useRef(asyncFn);

  asyncFnRef.current = asyncFn;

  const reset = useCallback(() => {
    setError(null);
  }, []);

  const execute = useCallback(async (...args) => {
    if (isPendingRef.current) {
      return undefined;
    }

    isPendingRef.current = true;
    setIsPending(true);
    setError(null);

    const controller = new AbortController();
    abortRef.current = controller;

    let retainPending = false;
    try {
      const fn = asyncFnRef.current;
      if (!fn) {
        return undefined;
      }
      const result = await fn(...args);
      if (result === KEEP_PENDING) {
        retainPending = true;
      }
      return result;
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setError(err);
      }
      throw err;
    } finally {
      if (!retainPending) {
        isPendingRef.current = false;
        setIsPending(false);
      }
      abortRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return { execute, isPending, error, reset };
}
